<template>
  <div class="h-full full-size-container">
    <v-card
      border
      class="pa-1 pb-4 pa-md-4 mb-5"
    >
      <v-card-title class="no-ellipsis"> {{ t('leads.master.page-title') }}</v-card-title>
      <v-card-text
        class="d-flex flex-wrap justify-space-between align-center gap-4"
        :class="$vuetify.display.smAndDown ? 'flex-column' : ''"
      >
        <div class="d-flex flex-wrap align-center gap-4">
          <v-text-field
            v-model="search"
            min-width="300"
            class="ma-2"
            density="compact"
            :placeholder="$t('labels.leads.lead-search')"
            hide-details
            :disabled="isLoading"
          />
          <v-select
            v-model="getContactsFilters.currentStatus"
            :items="leadsStatuses"
            item-title="text"
            item-value="value"
            min-width="300"
            :label="t('lead-list.filter-settings.labels.status')"
            class="ma-2"
            hide-details
            clearable
            variant="outlined"
            density="compact"
            :disabled="isLoading"
          />
          <v-select
            v-model="getContactsFilters.assignedUser"
            :items="assignedUsers"
            item-title="text"
            item-value="value"
            min-width="300"
            :label="$t('lead-list.filter-settings.labels.assigned-employee')"
            class="ma-2"
            hide-details
            clearable
            variant="outlined"
            density="compact"
            :disabled="isLoading"
          />
          <v-icon
            class="refresh-icon d-none d-sm-inline-flex"
            @click="(resetFilters(), (isActiveMoveContacts = false))"
          >
            mdi mdi-refresh
          </v-icon>
        </div>
        <div class="d-flex align-center gap-2 mt-4 mt-md-0">
          <v-btn
            class="camel-case"
            color="primary"
            variant="outlined"
            :disabled="isLoading"
            @click="isActiveMoveContacts = !isActiveMoveContacts"
          >
            <v-icon start> mdi mdi-domain-plus </v-icon>
            <span>{{ $t('leads.master.button.move') }}</span>
          </v-btn>
          <v-icon
            class="refresh-icon d-inline-flex d-sm-none"
            @click="(resetFilters(), (isActiveMoveContacts = false))"
          >
            mdi mdi-refresh
          </v-icon>
        </div>
      </v-card-text>
    </v-card>

    <div
      :class="{ 'flex-column': $vuetify.display.smAndDown }"
      :style="{
        gap: isActiveMoveContacts ? '10px' : '0px',
        display: isActiveMoveContacts ? 'flex' : '',
      }"
    >
      <v-slide-x-transition>
        <div
          v-if="isActiveMoveContacts"
          :class="{
            'list-tool-width': $vuetify.display.mdAndUp,
            'w-full mb-10': $vuetify.display.smAndDown,
          }"
        >
          <BulkMoveContacts
            @updateFetchIdsParameters="updateFetchIdsParameters"
            :has-crm="isCrmActive"
            :crm-name="readableCrmName"
            :has-applicable-leads="leadsList?.length > 0"
            :selected-lead-ids="
              isCustomSelectionEnable ? selectedValue?.map((value) => value.id) : selectedLeadIds
            "
            :active-child-company="activeChildCompany"
            @close="isActiveMoveContacts = false"
            @contactsMoved="(resetFilters(), resetMoveContactsDialog(), resetLeads())"
            @isLoading="isLoading = $event"
          >
            <template #selectAll>
              <v-card
                border
                style="border-radius: 10px !important"
              >
                <v-card-text class="pa-2">
                  <div>
                    <span
                      v-html="
                        $t('lead-list.some-contacts-selected', {
                          amount: selectedLeadIds.length || 0,
                        })
                      "
                    />
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </BulkMoveContacts>
        </div>
      </v-slide-x-transition>
      <v-card
        class="pa-4"
        style="margin-bottom: 40px"
        :class="{
          'list-width': $vuetify.display.mdAndUp && isActiveMoveContacts,
          'w-full': $vuetify.display.smAndDown,
        }"
      >
        <v-card-text>
          <div>
            <v-data-table-server
              v-model:items-per-page="itemsPerPage"
              v-model:selected="selectedValue"
              :show-select="isCustomSelectionEnable"
              :items-per-page-text="t('leads.master.table.page-text')"
              :headers="headers"
              :items="leadsList"
              :loading="leadsListLoading"
              :search="search"
              :no-data-text="noDataText"
              :items-length="totalLeads"
              @update:options="loadLeads($event)"
              @click:row="navigateToLeadDetails"
              @change="selectAllVisibleItems"
              :items-per-page-options="[10, 20, 50, 100]"
            >
              <template #[`item.data-table-select`]="{ item }">
                <v-checkbox
                  class="checkbox-class"
                  :ripple="false"
                  color="primary"
                  :model-value="selectedValue.map((value) => value.id).includes(item.id)"
                  @update:model-value="(v) => handleItemSelection(item, v)"
                  @click.stop
                  :disabled="isLoading"
                />
              </template>
              <template #[`item.zip`]="{ item }">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <div
                      class="text-truncate"
                      style="min-width: 50px; max-width: 120px"
                      v-bind="props"
                    >
                      {{ formatZip(item) }}
                    </div>
                  </template>
                  <span>{{ formatZip(item) }}</span>
                </v-tooltip>
              </template>
              <template v-slot:tfoot />
            </v-data-table-server>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>
<script setup lang="ts">
// @ts-nocheck
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, inject, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { VDataTable } from 'vuetify/components'

import { PROVIDERS } from '@/lib/crm'
import { formatDate } from '@/lib/dateTime'
import status, { defaultSystemStatuses, statusForSelect as statusList } from '@/lib/leadStatus'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { sortByString } from '@/lib/sort-functions'
import { InvitationContactFilter } from '@/modules/leads/enums/Invitation.js'
import { NEW_CONTACT_STATUSES } from '@/modules/leads/enums/Status'
import GET_LEADS_PIPELINE_STATUSES from '@/modules/leads/LeadList/Cards/queries/GetLeadsPipelineStatuses.gql'
import ACQUISITION_BOOSTER_LEAD_IDS from '@/modules/leads/queries/AcquisitionBoosterLeadIds.gql'
import USERS from '@/modules/leads/queries/Users.gql'
import BulkMoveContacts from '@/modules/master/leads/BulkMoveContacts.vue'

import LEADS_MASTER from '../queries/LeadsMaster.gql'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const $auth = inject('auth')
const selectedLeadIds = ref([])
const itemsPerPage = ref(20)
const selectedValue = ref([])
const isHandlingRowCheckbox = ref(false)
const headers = ref<InstanceType<typeof VDataTable>['$props']['headers']>([
  {
    title: t('leads.master.table.seventh-column'),
    key: 'company.name',
    align: 'start',
    sortable: false,
  },
  {
    title: t('leads.master.table.first-column'),
    align: 'start',
    key: 'firstname',
    sortable: false,
  },
  {
    title: t('leads.master.table.second-column'),
    align: 'start',
    key: 'lastname',
    sortable: false,
  },
  {
    title: t('leads.master.table.third-column'),
    align: 'start',
    key: 'currentStatus',
    sortable: false,
  },
  {
    title: t('leads.master.table.forth-column'),
    key: 'email',
    align: 'start',
    sortable: false,
  },
  {
    title: t('leads.master.table.zip'),
    key: 'zip',
    align: 'start',
    sortable: false,
  },
  {
    title: t('leads.master.table.fifth-column'),
    key: 'createdAt',
    align: 'start',
    sortable: false,
  },
  {
    title: t('leads.master.table.sixth-column'),
    key: 'lastConversionAt',
    align: 'start',
    sortable: false,
  },
])
const noDataText = ref(t('leads.master.no-leads'))
const isActiveMoveContacts = ref(false)
const isLoading = ref(false)
const crmConfigData = inject('crmConfigData')
const isCrmActive = computed(() => crmConfigData?.value?.isActive)
const readableCrmName = computed(() => {
  const provider = crmConfigData?.value?.crmProvider
  return PROVIDERS.find(({ value }) => value === provider)?.text || 'Ihrem CRM'
})

const isCustomSelectionEnable = ref(false)
const moveContactsFilters = ref({ moveContacts: true })
const getContactsFilters = reactive({
  currentStatus: null,
  assignedUser: null,
})

const name = ref('')
const search = ref('')
const routeParamId = computed(() => route?.params?.id || null)

const updateFetchIdsParameters = async ({ contactFilter, showFilter }) => {
  isCustomSelectionEnable.value = false
  selectedLeadIds.value = []

  if (showFilter) {
    isCustomSelectionEnable.value = true
    return
  }

  moveContactsFilters.value = { moveContacts: true }
  if (contactFilter === InvitationContactFilter.PROPERTY_REQUEST)
    moveContactsFilters.value = { ...moveContactsFilters.value, propertyRequests: true }
  else if (contactFilter === InvitationContactFilter.VALUATIONS)
    moveContactsFilters.value = { ...moveContactsFilters.value, valuations: true }
  else if (contactFilter === InvitationContactFilter.NEW)
    moveContactsFilters.value = {
      ...moveContactsFilters.value,
      currentStatus: NEW_CONTACT_STATUSES,
    }
  else if (contactFilter === InvitationContactFilter.CRM)
    moveContactsFilters.value = { ...moveContactsFilters.value, leadOriginCrm: true }

  await updateMoveContactsParameters()
}

const { mutate: acquisitionBoosterLeadIds } = useMutation(ACQUISITION_BOOSTER_LEAD_IDS)
const updateMoveContactsParameters = async () => {
  try {
    const {
      data: { getAcquisitionBoosterLeadIds },
    } = await acquisitionBoosterLeadIds({
      input: {
        companyId: $auth.user.companyId,
        filters: moveContactsFilters.value,
        childCompanyId: routeParamId.value,
      },
    })
    selectedLeadIds.value = getAcquisitionBoosterLeadIds.leadIds || []
  } catch (error) {
    showSnackbarMessage('error', t('alerts.acquisition-booster.error'))
  }
}

const variables = ref({
  page: 0,
  sortBy: 'lastConversionAt',
  sortDesc: true,
  itemsPerPage: 20,
  search: name.value,
  filters: getContactsFilters,
  childCompanyId: routeParamId.value,
})

const { result: statusesListResult } = useQuery(GET_LEADS_PIPELINE_STATUSES, {
  input: { isMasterView: false, childCompanyId: null },
})

const leadsStatuses = computed(() => {
  return (
    statusesListResult.value?.leadsPipelineStatuses.map((item) => {
      return mapStatusList(item.name)
    }) || []
  )
})

const usersVariables = computed(() => ({
  input: {
    companyId: $auth.user.companyId,
    isMasterView: route.path.startsWith('/master'),
    childCompanyId: routeParamId.value ?? null,
  },
}))
const { result: usersListResult } = useQuery(USERS, usersVariables)

const assignedUsers = computed(() => {
  const assignedUserList = (usersListResult?.value?.users ?? [])
    .map((user) => ({
      value: user.id,
      text: `${user.firstname} ${user.lastname}`,
    }))
    .sort(sortByString('text'))

  return assignedUserList
})

const mapStatusList = (name) => {
  if (defaultSystemStatuses.includes(name)) {
    const matchedStatus = Object.values(statusList).find((status) => status.value === name)

    if (matchedStatus) {
      return {
        value: matchedStatus.value,
        text: t(matchedStatus.text),
      }
    }
  }

  return { value: name, text: name }
}
const {
  result: leadsListResult,
  loading: leadsListLoading,
  refetch: leadsMasterRefetch,
} = useQuery(LEADS_MASTER, {
  input: variables,
})

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (!newId || newId !== oldId) {
      resetFilters()
      resetMoveContactsDialog()
      variables.value = {
        ...variables.value,
        childCompanyId: newId ? newId : null,
      }
      resetLeads()
    }
  }
)

const totalLeads = computed(() => {
  return leadsListResult.value?.leadsMaster?.total || 0
})

const leadsList = computed(() => {
  return (
    leadsListResult.value?.leadsMaster?.entries.map((lead) => {
      return {
        ...lead,
        currentStatus: status[lead.currentStatus]
          ? t(status[lead.currentStatus])
          : lead.currentStatus,
        createdAt: lead.createdAt ? formatDate(lead.createdAt, 'dateShort') : '',
        lastConversionAt: lead.lastConversionAt
          ? formatDate(lead.lastConversionAt, 'dateShort')
          : '',
      }
    }) || []
  )
})

const activeChildCompany = computed(() => {
  return routeParamId.value ? leadsList.value[0]?.company : null
})

const loadLeads = (settings) => {
  if (variables.value.itemsPerPage !== settings.itemsPerPage) {
    variables.value.page = 1
  } else {
    variables.value.page = settings.page
  }
  variables.value.itemsPerPage = itemsPerPage.value
}

let debounceTimeout: ReturnType<typeof setTimeout>
watch(search, (val) => {
  if (debounceTimeout) clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    variables.value.search = val
    variables.value.page = 1
  }, 400)
})

const navigateToLeadDetails = (event, lead) => {
  if (isLoading.value) {
    return
  }

  const { item } = lead
  router.push({
    name: 'master-lead-details',
    params: { id: item.id, childCompanyId: routeParamId.value },
  })
}

let rowCheckboxClickTimeoutTimeout = null
const handleItemSelection = (item, selected) => {
  if (isLoading.value) {
    return
  }

  isHandlingRowCheckbox.value = true
  if (selected) {
    selectedValue.value = [...selectedValue.value, item]
  } else {
    selectedValue.value = selectedValue.value.filter((i) => i.id !== item.id)
  }

  rowCheckboxClickTimeoutTimeout = setTimeout(() => {
    isHandlingRowCheckbox.value = false
  }, 0)
}

const selectAllVisibleItems = (event) => {
  if (isHandlingRowCheckbox.value || isLoading.value) {
    return
  }
  const isChecked = event?.target?.checked
  if (isChecked) {
    selectedValue.value = [...leadsList.value]
  } else {
    selectedValue.value = []
  }
}
const resetLeads = () => {
  leadsMasterRefetch({
    input: {
      ...variables.value,
      filters: {
        currentStatus: null,
        assignedUser: null,
      },
    },
  })
}
const resetFilters = () => {
  getContactsFilters.currentStatus = null
  getContactsFilters.assignedUser = null
  search.value = ''
  variables.value.search = ''
  variables.value.page = 1
}

const resetMoveContactsDialog = () => {
  selectedValue.value = []
  selectedLeadIds.value = []
  isCustomSelectionEnable.value = false
}

const formatZip = (item) => {
  const valuationConversion = item.conversions.find((conversion) => {
    return conversion.type === 'VALUATION'
  })
  if (valuationConversion) {
    return valuationConversion.valuation.inputs.address.zip
  }
  return ''
}

watch(
  () => selectedValue.value,
  () => {
    selectedLeadIds.value = selectedValue.value.map((value) => value.id)
  },
  { deep: true }
)

watch(
  () => totalLeads.value,
  () => {
    if (totalLeads.value === 0) {
      resetMoveContactsDialog()
      isActiveMoveContacts.value = false
    }
  },
  { deep: true }
)

watch(
  () => isActiveMoveContacts.value,
  () => {
    if (isActiveMoveContacts.value) {
      resetMoveContactsDialog()
    }
  },
  { deep: true }
)

onUnmounted(() => {
  if (rowCheckboxClickTimeoutTimeout) {
    clearTimeout(rowCheckboxClickTimeoutTimeout)
  }
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
})
</script>
<style scoped>
.refresh-icon {
  cursor: pointer;
  align-self: center;
  font-size: 25px;
}
.full-size-container {
  --list-tool-width: 380px;
  --list-gap: 10px;
}

.list-tool-width {
  min-width: var(--list-tool-width);
  max-width: var(--list-tool-width);
}
.list-width {
  min-width: calc(100% - var(--list-tool-width) - var(--list-gap));
  max-width: calc(100% - var(--list-tool-width) - var(--list-gap));
}
.no-ellipsis {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}

.checkbox-class {
  height: 55px;
}

@media (max-width: 960px) {
  .v-card-text {
    padding: 0 !important;
  }
}
</style>
